import { call, put, select, takeLatest } from 'typed-redux-saga'
import { errorHandler, notifyMessageCreator, gridSlice } from 'Modules'
import { ResponseCatalog, RequestParams } from 'Models'
import { FormNames } from 'Src/constants/FormNames'
import { Callback } from 'Src/types/utilTypes'
import { FrontUrls } from 'Constants/FrontUrls'
import { routerActions } from 'connected-react-router'
import {
	ServiceNotification,
	ServiceNotificationsNS,
	serviceNotificationsSlice,
	serviceNotificationsApi,
	serviceNotificationsSelectors,
} from 'Pages/admin'

function* getAllServiceNotificationsSaga(
	requestParams?: RequestParams,
	clear?: boolean,
	callback?: Callback
) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		let params = new RequestParams()
		let oldItems = new ResponseCatalog<ServiceNotification>()
		if (!clear && requestParams?.page !== 1) {
			oldItems = yield* select(serviceNotificationsSelectors.selectList)
			params = yield* select(serviceNotificationsSelectors.selectRequestParams)
		}
		const res = yield* call(
			serviceNotificationsApi.getAllServiceNotifications,
			requestParams || params
		)
		const items = new ResponseCatalog<ServiceNotification>(res)
		const newItems = res.entries.map((item) => new ServiceNotification(item))
		items.entries = [...oldItems.entries, ...newItems]
		yield* put(serviceNotificationsSlice.actions.setList(items))
		yield* put(serviceNotificationsSlice.actions.setRequestParams(requestParams || params))
		callback?.()
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getServiceNotificationsSaga(action: ServiceNotificationsNS.GetList) {
	try {
		yield* put(gridSlice.actions.setLoading(true))
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		const { params, clear, callback } = action.payload
		yield* call(getAllServiceNotificationsSaga, params, clear, callback)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
		yield* put(gridSlice.actions.setLoading(false))
	}
}

function* getServiceNotificationSaga(action: ServiceNotificationsNS.GetNotification) {
	try {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		const res = yield* call(serviceNotificationsApi.getServiceNotification, action.payload)
		const notification = new ServiceNotification(res)
		yield* put(serviceNotificationsSlice.actions.setNotification(notification))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
	}
}

function* createServiceNotificationSaga(action: ServiceNotificationsNS.CreateNotification) {
	try {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		const res = yield* call(serviceNotificationsApi.createServiceNotification, action.payload)
		const notification = new ServiceNotification(res)
		yield* put(serviceNotificationsSlice.actions.setNotification(notification))
		yield* call(notifyMessageCreator, 'service_notification.create.success')
		yield* put(
			routerActions.push(
				FrontUrls.admin_panel.notifications_edit.replace(':id', String(notification.id))
			)
		)
	} catch (error) {
		yield* call(errorHandler, error, FormNames.POPUPGROUP.POPUP_EDIT_FORM)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
	}
}

function* updateServiceNotificationSaga(action: ServiceNotificationsNS.UpdateNotification) {
	try {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		const { data, isGrid = false } = action.payload
		const res = yield* call(serviceNotificationsApi.updateServiceNotification, data)
		if (!isGrid) {
			const notification = new ServiceNotification(res)
			yield* put(serviceNotificationsSlice.actions.setNotification(notification))
		} else {
			yield* call(getAllServiceNotificationsSaga, new RequestParams(), true)
		}
		yield* call(notifyMessageCreator, 'service_notification.update.success')
	} catch (error) {
		yield* call(errorHandler, error, FormNames.POPUPGROUP.POPUP_EDIT_FORM)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
	}
}

function* deleteServiceNotificationSaga(action: ServiceNotificationsNS.DeleteNotification) {
	try {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		yield* call(serviceNotificationsApi.deleteServiceNotification, action.payload)
		yield* put(serviceNotificationsSlice.actions.clearList())
		yield* call(getAllServiceNotificationsSaga, new RequestParams(), true)
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
	}
}

function* readServiceNotificationSaga(action: ServiceNotificationsNS.ReadNotification) {
	try {
		const { id, isComplete } = action.payload
		if (isComplete) yield* call(serviceNotificationsApi.readServiceNotification, id)
	} catch (error) {
		yield* call(errorHandler, error)
	}
}

function* getActiveServiceNotificationsSaga(action: ServiceNotificationsNS.GetActiveList) {
	try {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(true))
		const res = yield* call(
			serviceNotificationsApi.getAllServiceNotifications,
			new RequestParams(1, 30, null, { active: true })
		)
		const items = new ResponseCatalog<ServiceNotification>(res)
		yield* put(serviceNotificationsSlice.actions.setActiveList(items))
	} catch (error) {
		yield* call(errorHandler, error)
	} finally {
		yield* put(serviceNotificationsSlice.actions.setIsLoading(false))
	}
}

export function* serviceNotificationsSaga() {
	yield* takeLatest(serviceNotificationsSlice.actions.getList.type, getServiceNotificationsSaga)
	yield* takeLatest(
		serviceNotificationsSlice.actions.getNotification.type,
		getServiceNotificationSaga
	)
	yield* takeLatest(
		serviceNotificationsSlice.actions.createNotification.type,
		createServiceNotificationSaga
	)
	yield* takeLatest(
		serviceNotificationsSlice.actions.updateNotification.type,
		updateServiceNotificationSaga
	)
	yield* takeLatest(
		serviceNotificationsSlice.actions.deleteNotification.type,
		deleteServiceNotificationSaga
	)
	yield* takeLatest(
		serviceNotificationsSlice.actions.readNotification.type,
		readServiceNotificationSaga
	)
	yield* takeLatest(
		serviceNotificationsSlice.actions.getActiveList.type,
		getActiveServiceNotificationsSaga
	)
}
